// Pure helpers for building the qualifying sheet (QUAL view). Kept free of React
// so the row-building logic can be unit-tested in isolation.

import {
  getCategoryForClass,
  getClassIndex,
  getFinalEt,
  getOverUnder,
  getOverUnderMs,
} from "./raceIndex";

// A qualifying round is any round whose name starts with "q"/"Q".
export const isQualifyingRound = (round) => {
  if (!round) return false;
  const first = String(round).trim().charAt(0).toLowerCase();
  return first === "q";
};

// Distinct qualifying round names (trimmed) from a flat list of round strings.
// Deduped case-insensitively — the search round filter is case-insensitive, so
// "Q1" and "q1" would fetch the same rows; keep the first-seen spelling.
export const qualifyingRoundNames = (rounds) => {
  const seen = new Set();
  const names = [];
  for (const raw of rounds || []) {
    if (!isQualifyingRound(raw)) continue;
    const name = String(raw).trim();
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
};

// Over/under (stored as whole milliseconds) formatted in seconds with a sign,
// e.g. +0.012 / -0.050, or "-" when unavailable. Shared by the card display and
// the share caption so they never diverge.
export const formatOverUnderSeconds = (overUnderMs) =>
  overUnderMs == null
    ? "-"
    : (overUnderMs >= 0 ? "+" : "") + (overUnderMs / 1000).toFixed(3);

// Compact text caption that rides along with a shared image (and stands alone in
// text-only share targets / the desktop clipboard fallback). Lists the top
// qualifiers by over/under; the image carries the full field.
export const buildQualShareText = (rows, { trackName, eventName, tab } = {}) => {
  const lines = ["raceresults.1320cc.com"];
  if (trackName) lines.push(trackName);
  if (eventName) lines.push(eventName);
  lines.push(`${tab} — Unofficial Qualifying`);
  lines.push("");
  const list = rows || [];
  list.slice(0, 10).forEach((r, i) => {
    lines.push(`${i + 1}. ${r.driver || "-"}  ${formatOverUnderSeconds(r.overUnderMs)}`);
  });
  if (list.length > 10) lines.push(`…and ${list.length - 10} more`);
  return lines.join("\n");
};

const sanitizeFilePart = (s) =>
  String(s || "")
    .trim()
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "");

// Filename for a shared/downloaded qualifying sheet, e.g.
// "Unofficial-Qual-Track_Name-Super_Stock-2026-07-21.png".
export const buildQualShareFileName = ({ trackName, tab, dateIso } = {}) => {
  const track = sanitizeFilePart(trackName) || "sheet";
  const cat = sanitizeFilePart(tab);
  return `Unofficial-Qual-${track}${cat ? `-${cat}` : ""}${dateIso ? `-${dateIso}` : ""}.png`;
};

// Drop duplicate run records that can appear when merging per-round search
// results (or overlapping pages). Each lane row has a unique raceLaneResultId;
// fall back to a composite key when it's absent.
export const dedupeRuns = (runs) => {
  const seen = new Set();
  const out = [];
  for (const r of runs || []) {
    const key =
      r.raceLaneResultId != null
        ? `id:${r.raceLaneResultId}`
        : `${r.raceKey}|${r.driver}|${r.carNo}|${r.round}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

// Build one row per driver for a category tab from raw run records:
//   - qualifying rounds only (round starts with q/Q),
//   - class must resolve to the selected category in nhraIndexes.json,
//   - keep each driver's fastest-ET run,
//   - ordered by over/under ascending (then ET, then name).
// Runs whose class isn't in the index table (or with no final ET) are dropped.
export const buildQualRows = (runs, tab) => {
  const best = new Map();

  for (const run of runs || []) {
    if (!isQualifyingRound(run.round)) continue;

    const carClass = run.carClass;
    const category = getCategoryForClass(carClass);
    if (category !== tab) continue;

    const raceDate = run.raceUniqueId || run.raceDate;
    const ft4 = run.ft4;
    const ft8 = run.ft8;

    const finalEt = getFinalEt(carClass, raceDate, ft4, ft8);
    const overUnderMs = getOverUnderMs(carClass, raceDate, ft4, ft8);
    if (finalEt == null || overUnderMs == null) continue;

    const driver = run.driver || "";
    const carNo = run.carNo || "";
    const key = `${driver}||${carNo}`;

    const existing = best.get(key);
    if (!existing || finalEt < existing.finalEt) {
      best.set(key, {
        driver,
        carNo,
        category,
        carClass,
        index: getClassIndex(carClass, raceDate, ft4),
        finalEt,
        overUnder: getOverUnder(carClass, raceDate, ft4, ft8),
        overUnderMs,
      });
    }
  }

  return Array.from(best.values()).sort(
    (a, b) =>
      a.overUnderMs - b.overUnderMs ||
      a.finalEt - b.finalEt ||
      a.driver.localeCompare(b.driver),
  );
};
